import React, { useEffect, useState } from 'react';
import { List, message, Switch, Typography } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Card, CStatus, CType } from 'entities/card/model/types';
import { useCloseCardMutation, useGetCardRulesQuery, useSetCardRuleMutation } from '../api';
import { Rules } from '../model/types';
import { useDispatch, useSelector } from 'react-redux';
import { getValidateConfirmation } from 'shared/config/store/reducers/userSlice';
import { ValidationMethod } from 'entities/user/model/types';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { getEMICurrentAccountObject } from 'shared/config/store/reducers/accountsSlice';
import { getAccountCards, updateCard } from 'shared/config/store/reducers/cardsSlice';
import { RootState } from 'shared/config/store/store';
import { Account } from 'entities/account/model/types';
import { PinDrawer, useGetPin } from 'features/card/get-pin';
import { RULES_TRANSLATE } from '../model/const';
import { useResultModal } from 'shared/libs/hooks/useResultModal/useResultModal';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { ApiError } from 'shared/model/types';

interface Props {
  accountId: string;
  cardId: string;
  cardType: CType;
  cardStatus: CStatus;
}

const CardRules: React.FC<Props> = ({ accountId, cardId, cardType, cardStatus }) => {
  const { formatMessage: t } = useIntl();
  const { showResult } = useResultModal();
  const navigate = useNavigate();
  const [rules, setRules] = useState<Record<Rules, boolean>>({
    [Rules.Atm]: false,
    [Rules.ContactlessPayments]: false,
    [Rules.Ecommerce]: false,
  });

  const dispatch = useDispatch();
  const { data, isLoading } = useGetCardRulesQuery({ accountId, cardId }, { skip: !accountId || !cardId });
  const [setCardRule, { isLoading: isMutating }] = useSetCardRuleMutation();
  const [closeCard] = useCloseCardMutation();

  const validateConfirmation = useSelector(getValidateConfirmation);
  const isTwoFactorEnabled = validateConfirmation === ValidationMethod.TwoFA;
  const showRules = cardStatus === CStatus.Active || CStatus.SoftBlocked;
  const isActive = cardStatus === CStatus.Active; 

  const currentEmi = useSelector(getCurrentEmi);
  const currentAccount: Account = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi)
  ) as Account;
  const card: Card = useSelector(getAccountCards(currentEmi, currentAccount.id))?.[0] as Card;

  const {
    drawerVisible,
    setDrawerVisible,
    codeInputRef,
    handleGetPinClick,
    handleCodeSubmit,
    handleCodeResend,
    pin
  } = useGetPin(accountId, Number(cardId));


  useEffect(() => {
    if (data?.response) {
      const getBoolean = (num: 'false' | 'true') => num === 'true';
      setRules({
        [Rules.Atm]: getBoolean(data.response.atm_disabled),
        [Rules.ContactlessPayments]: getBoolean(data.response.contactless_disabled),
        [Rules.Ecommerce]: getBoolean(data.response.ecommerce_disabled),
      });
    }
  }, [data]);

  const toggleRule = async (name: Rules) => {
    try {
      await setCardRule({
        accountId,
        cardId,
        name,
        value: rules[name] ? 0 : 1,
      }).unwrap();
      setRules((prev) => ({ ...prev, [name]: !prev[name] }));
      message.success(t({ id: 'common.message.success' }));
    } catch (err) {
      message.error(t({ id: 'common.message.error' }));
    }
  };

  const onHandleClose = () => {
    dispatch(setActivity(true));
    closeCard({
      accountId,
      cardId
    })
      .unwrap()
      .then(() => {
        dispatch(
          updateCard({
            accountId: currentAccount.id,
            emi: currentEmi,
            cardId: card.id,
            newCard: { status: CStatus.Closed },
          })
        );
        message.success(t({ id: 'card.close.success' }));
        // Navigate to account page after showing success notification
        setTimeout(() => {
          navigate(`/account/${currentAccount.id}`);
        }, 500);
      })
      .catch((err: ApiError) => {
        message.error(err.data.errors?.[0].defaultMessage ?? t({ id: 'card.close.failed' }));
      })
      .finally(() => {
        dispatch(setActivity(false));
      });
  }

  const items = [
    ...(cardType === CType.Physical && showRules
      ? [
        ...(isActive
          ? [
              {
                key: 'get_pin',
                label: t({ id: 'card.cardRules.getPin' }),
                onClick: handleGetPinClick,
                renderIcon: true,
              },
            ]
          : []),

        {
          key: Rules.Atm,
          label: t({ id: RULES_TRANSLATE[Rules.Atm] }),
          children: (
            <Switch
              checked={rules[Rules.Atm]}
              onChange={() => toggleRule(Rules.Atm)}
              loading={isMutating}
            />
          ),
        },
        {
          key: Rules.ContactlessPayments,
          label: t({ id: RULES_TRANSLATE[Rules.ContactlessPayments] }),
          children: (
            <Switch
              checked={rules[Rules.ContactlessPayments]}
              onChange={() => toggleRule(Rules.ContactlessPayments)}
              loading={isMutating}
            />
          ),
        },
      ]
      : []),
    ...(showRules
      ? [
        {
          key: Rules.Ecommerce,
          label: t({ id: RULES_TRANSLATE[Rules.Ecommerce] }),
          children: (
            <Switch
              checked={!rules[Rules.Ecommerce]}
              onChange={() => toggleRule(Rules.Ecommerce)}
              loading={isMutating}
            />
          ),
        },
      ]
      : []),
    {
      key: 'card-close',
      label: t({ id: 'cardRules.closeCard' }),
      onClick: () => showResult({ type: 'info', title: t({ id: 'card.close.title' }), content: t({ id: 'card.close.subtitle' }), action: true, actionButton: { onClick: onHandleClose, color: 'red', variant: 'solid', children: t({ id: 'cardRules.closeCard' }), block: true } }),
      renderIcon: true
    },
  ];

  return (
    <div style={{ marginTop: 32 }}>
      <Typography.Title level={5} style={{ marginBottom: 16 }}>
        {t({ id: 'card.cardRules.title' })}
      </Typography.Title>
      <List
        itemLayout="horizontal"
        dataSource={items}
        loading={isLoading}
        renderItem={(item) => (
          <List.Item
            key={item.key}
            onClick={item.onClick}
            style={{ cursor: item.onClick ? 'pointer' : 'default' }}
          >
            <List.Item.Meta title={item.label} />
            {item.children ?? (item.renderIcon && <RightOutlined />)}
          </List.Item>
        )}
      />

      <PinDrawer
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        isTwoFactorEnabled={isTwoFactorEnabled}
        pin={pin}
        codeInputRef={codeInputRef}
        handleCodeSubmit={handleCodeSubmit}
        handleCodeResend={handleCodeResend}
      />
    </div>
  );
};

export default CardRules;
